import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, MapPin, Download } from "lucide-react";
import { Reveal } from "./Reveal";

const PDF_URL = "https://customer-assets-agu9un31.emergentagent.net/job_timbodaking-media/artifacts/dokc2y8x_CreatorVerse%20Timbaland%20Press%20Release.pdf";
const TICKETS_URL = "https://timbalandwayweekend.com";

export default function EventSection({ event = {} }) {
  return (
    <section id="event" className="relative overflow-hidden py-24 md:py-40" data-testid="event-section">
      <div className="absolute inset-0">
        {event.image && <img src={event.image} alt="event" className="w-full h-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/80 to-obsidian" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 40%, rgba(79, 176, 74,0.12), transparent 55%)" }} />
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="overline text-gold mb-6">{event.tagline}</p>
          <img
            src="/tww-logo.png"
            alt={event.title}
            data-testid="event-logo"
            className="w-full max-w-[340px] md:max-w-[520px] h-auto object-contain mb-8"
          />
        </Reveal>

        <Reveal delay={0.1} className="flex flex-wrap gap-x-10 gap-y-4 mb-10">
          <span className="flex items-center gap-3 font-display uppercase tracking-[0.15em] text-white/80 text-sm">
            <Calendar size={18} className="text-gold" /> {event.date}
          </span>
          <span className="flex items-center gap-3 font-display uppercase tracking-[0.15em] text-white/80 text-sm">
            <MapPin size={18} className="text-gold" /> {event.location}
          </span>
        </Reveal>

        <Reveal delay={0.15}>
          <p className="max-w-2xl text-white/70 text-lg md:text-xl leading-relaxed font-body mb-12">
            {event.description}
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="overline text-gold mb-5">CreatørVerse · October 24 &amp; 25, 2026</p>
          <h3 className="headline text-3xl md:text-5xl text-white mb-8">Get the full schedule.</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={TICKETS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="get-tickets-btn"
              className="group inline-flex items-center gap-3 bg-gold text-black font-display uppercase tracking-[0.2em] text-sm px-9 py-4 hover:bg-white transition-colors duration-300 rounded-full"
            >
              Tickets &amp; Full Schedule
              <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <a
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="press-release-download"
              className="group inline-flex items-center gap-3 border border-white/25 text-white font-display uppercase tracking-[0.2em] text-sm px-9 py-4 hover:bg-white/10 transition-colors duration-300 rounded-full"
            >
              <Download size={18} /> Download Press Release
            </a>
          </div>
        </Reveal>

        {event.stats?.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mt-20 border border-white/10">
            {event.stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-obsidian p-8 md:p-10"
                data-testid={`event-stat-${i}`}
              >
                <p className="font-display font-700 text-4xl md:text-5xl text-gold mb-2">{s.value}</p>
                <p className="font-display uppercase tracking-[0.15em] text-[0.68rem] text-white/55">{s.label}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
